import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { getApiUrl } from '../../utils/api';

import './Reserve.css';

const MIN_NIGHTS = 3;
const MAX_NIGHTS = 100;

function Reserve() {
  const [calendarDays, setCalendarDays] = useState([]);
  const [status, setStatus] = useState('loading');
  const [isCalendarUpdating, setIsCalendarUpdating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const loadedCalendarRangesRef = useRef(new Set());
  const hasLoadedCalendarRef = useRef(false);

  const [visibleMonth, setVisibleMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guestsCount, setGuestsCount] = useState(4);
  const [youngChildrenCount, setYoungChildrenCount] = useState(0);
  const [babyCotRequested, setBabyCotRequested] = useState(false);

  const [quoteStatus, setQuoteStatus] = useState('idle');
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState('');

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchVisibleCalendar = async () => {
      const today = startOfDay(new Date());
      const firstVisibleDay = new Date(
        visibleMonth.getFullYear(),
        visibleMonth.getMonth(),
        1
      );
      const lastVisibleDay = new Date(
        visibleMonth.getFullYear(),
        visibleMonth.getMonth() + 2,
        0
      );

      const startDate = formatDate(
        firstVisibleDay < today ? today : firstVisibleDay
      );
      const endDate = formatDate(lastVisibleDay);
      const rangeKey = `${startDate}:${endDate}`;

      if (loadedCalendarRangesRef.current.has(rangeKey)) {
        return;
      }

      try {
        if (hasLoadedCalendarRef.current) {
          setIsCalendarUpdating(true);
        } else {
          setStatus('loading');
        }

        setErrorMessage('');

        const response = await fetch(
          getApiUrl(`/api/guesty/calendar?startDate=${startDate}&endDate=${endDate}`)
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Could not load calendar.');
        }

        if (!isCurrentRequest) {
          return;
        }

        const normalizedDays = normalizeGuestyCalendar(data);

        setCalendarDays((currentDays) =>
          mergeCalendarDays(currentDays, normalizedDays)
        );
        loadedCalendarRangesRef.current.add(rangeKey);
        hasLoadedCalendarRef.current = true;
        setStatus('success');
      } catch (error) {
        if (!isCurrentRequest) {
          return;
        }

        setErrorMessage(error.message);

        if (!hasLoadedCalendarRef.current) {
          setStatus('error');
        }
      } finally {
        if (isCurrentRequest) {
          setIsCalendarUpdating(false);
        }
      }
    };

    fetchVisibleCalendar();

    return () => {
      isCurrentRequest = false;
    };
  }, [visibleMonth]);

  const calendarMap = useMemo(() => {
    const map = new Map();

    calendarDays.forEach((day) => {
      map.set(day.date, day);
    });

    return map;
  }, [calendarDays]);

  const secondVisibleMonth = useMemo(() => {
    return addMonths(visibleMonth, 1);
  }, [visibleMonth]);

  const selectedNights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;

    return getNightsCount(checkIn, checkOut);
  }, [checkIn, checkOut]);

  const averageNightlyRate = useMemo(() => {
    const total = Number(quote?.total);

    if (!Number.isFinite(total) || selectedNights <= 0) {
      return null;
    }

    return total / selectedNights;
  }, [quote, selectedNights]);

  const selectedRangeHasUnavailableDates = useMemo(() => {
    if (!checkIn || !checkOut) return false;

    const dates = getDatesBetween(checkIn, checkOut);

    return dates.some((date) => !calendarMap.get(date)?.isAvailable);
  }, [calendarMap, checkIn, checkOut]);

  const selectedRangeHasInvalidNights = useMemo(() => {
    if (!checkIn || !checkOut) return false;

    return selectedNights < MIN_NIGHTS || selectedNights > MAX_NIGHTS;
  }, [checkIn, checkOut, selectedNights]);

  const handleDayClick = (dateString) => {
    const today = startOfDay(new Date());
    const clickedDate = parseDate(dateString);

    setQuote(null);
    setQuoteStatus('idle');
    setQuoteError('');

    if (clickedDate < today) return;
    if (!calendarMap.get(dateString)?.isAvailable) return;

    if (!checkIn || checkOut) {
      setCheckIn(dateString);
      setCheckOut('');
      setErrorMessage('');
      return;
    }

    if (clickedDate <= parseDate(checkIn)) {
      setCheckIn(dateString);
      setCheckOut('');
      setErrorMessage('');
      return;
    }

    const rangeDates = getDatesBetween(checkIn, dateString);
    const hasUnavailableDates = rangeDates.some(
      (date) => !calendarMap.get(date)?.isAvailable
    );

    if (hasUnavailableDates) {
      setErrorMessage(
        'Selected dates include unavailable nights. Please choose another period.'
      );
      return;
    }

    const nights = getNightsCount(checkIn, dateString);

    if (nights < MIN_NIGHTS || nights > MAX_NIGHTS) {
      setErrorMessage(
        `Please select a stay between ${MIN_NIGHTS} and ${MAX_NIGHTS} nights.`
      );
      return;
    }

    setErrorMessage('');
    setCheckOut(dateString);
  };

  const goToPreviousMonth = () => {
    setVisibleMonth((currentMonth) => {
      const firstCurrentMonth = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        1
      );

      if (currentMonth <= firstCurrentMonth) {
        return currentMonth;
      }

      return addMonths(currentMonth, -1);
    });
  };

  const goToNextMonth = () => {
    setVisibleMonth((currentMonth) => addMonths(currentMonth, 1));
  };

  const handleGetQuote = async () => {
    try {
      setQuoteStatus('loading');
      setQuoteError('');
      setQuote(null);

      const response = await fetch(getApiUrl('/api/guesty/quote'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          checkInDateLocalized: checkIn,
          checkOutDateLocalized: checkOut,
          guestsCount: Number(guestsCount),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Could not create quote.');
      }

      setQuote(data);
      setQuoteStatus('success');
    } catch (error) {
      setQuoteError(error.message);
      setQuoteStatus('error');
    }
  };

  return (
    <section className="reserve">
      <div className="reserve__intro">

        <p className="reserve__description">
          For direct booking and inquiries, please select your preferred dates
          or contact us directly.
        </p>
      </div>

      <div className="reserve__inner">
        <div className="reserve__calendar-card">
          <div className="reserve__calendar-header">
            <button
              type="button"
              className="reserve__calendar-arrow"
              onClick={goToPreviousMonth}
              aria-label="Previous month"
            >
              ‹
            </button>

            <div className="reserve__calendar-heading">
              <span>Availability calendar</span>
            </div>

            <button
              type="button"
              className="reserve__calendar-arrow"
              onClick={goToNextMonth}
              aria-label="Next month"
            >
              ›
            </button>
          </div>

          {status === 'loading' && (
            <p className="reserve__text">Loading availability...</p>
          )}

          {isCalendarUpdating && (
            <p className="reserve__text">Updating availability...</p>
          )}

          {status === 'error' && (
            <p className="reserve__error">{errorMessage}</p>
          )}

          {status === 'success' && (
            <div className="reserve__months">
              <CalendarMonth
                monthDate={visibleMonth}
                calendarMap={calendarMap}
                checkIn={checkIn}
                checkOut={checkOut}
                onDayClick={handleDayClick}
              />

              <CalendarMonth
                monthDate={secondVisibleMonth}
                calendarMap={calendarMap}
                checkIn={checkIn}
                checkOut={checkOut}
                onDayClick={handleDayClick}
              />
            </div>
          )}

          <div className="reserve__legend">
            <span>
              <i className="reserve__legend-dot reserve__legend-dot_available"></i>
              Available
            </span>

            <span>
              <i className="reserve__legend-dot reserve__legend-dot_unavailable"></i>
              Unavailable
            </span>

            <span>
              <i className="reserve__legend-dot reserve__legend-dot_selected"></i>
              Selected
            </span>
          </div>

          <p className="reserve__stay-note">
            Minimum stay is {MIN_NIGHTS} nights. Maximum stay is {MAX_NIGHTS} nights. For longer stays, please contact us.
          </p>
        </div>

        <aside className="reserve__summary">
          <span className="reserve__summary-label">Your stay</span>

          {!checkIn && (
            <>
              <h2>Select your dates</h2>
              <p>
                Choose your preferred check-in and check-out dates to start your
                direct booking request.
              </p>
            </>
          )}

          {checkIn && (
            <>
              <div className="reserve__summary-row">
                <span>Check-in</span>
                <strong>{formatDisplayDate(checkIn)}</strong>
              </div>

              <div className="reserve__summary-row">
                <span>Check-out</span>
                <strong>
                  {checkOut ? formatDisplayDate(checkOut) : 'Select date'}
                </strong>
              </div>

              <div className="reserve__summary-row">
                <span>Nights</span>
                <strong>{selectedNights || '—'}</strong>
              </div>

              <div className="reserve__guests">
                <span className="reserve__guests-title">Guests</span>

                <label className="reserve__guest-field">
                  <span>Guests</span>

                  <input
                    type="number"
                    min="1"
                    max="8"
                    value={guestsCount}
                    onChange={(event) => {
                      const value = Number(event.target.value);

                      if (value < 1 || value > 8) return;

                      setGuestsCount(value);
                      setQuote(null);
                      setQuoteStatus('idle');
                      setQuoteError('');
                    }}
                  />
                </label>

                <label className="reserve__guest-field">
                  <span>Children under 5</span>

                  <input
                    type="number"
                    min="0"
                    max="7"
                    value={youngChildrenCount}
                    onChange={(event) => {
                      const value = Number(event.target.value);

                      if (value < 0 || value > 7) return;

                      setYoungChildrenCount(value);
                      setQuote(null);
                      setQuoteStatus('idle');
                      setQuoteError('');
                    }}
                  />
                </label>

                <label className="reserve__checkbox">
                  <input
                    type="checkbox"
                    checked={babyCotRequested}
                    onChange={(event) => {
                      setBabyCotRequested(event.target.checked);
                      setQuote(null);
                      setQuoteStatus('idle');
                      setQuoteError('');
                    }}
                  />

                  <span>Baby cot/playpen requested</span>
                </label>

                <p className="reserve__guests-note">
                  Villa Mandragora accommodates up to 8 guests. Children under 5
                  are not included in this count if they do not require a
                  full-size bed.
                </p>
              </div>

              {errorMessage && (
                <p className="reserve__error">{errorMessage}</p>
              )}

              {selectedRangeHasUnavailableDates && (
                <p className="reserve__error">
                  Selected dates include unavailable nights.
                </p>
              )}

              {selectedRangeHasInvalidNights && (
                <p className="reserve__error">
                  Please select a stay between {MIN_NIGHTS} and {MAX_NIGHTS}{' '}
                  nights.
                </p>
              )}

              {checkIn &&
                checkOut &&
                !selectedRangeHasUnavailableDates &&
                !selectedRangeHasInvalidNights && (
                  <button
                    type="button"
                    className="reserve__button reserve__button_dark"
                    onClick={handleGetQuote}
                    disabled={quoteStatus === 'loading'}
                  >
                    {quoteStatus === 'loading'
                      ? 'Checking...'
                      : 'Check availability'}
                  </button>
                )}

              {quoteStatus === 'error' && (
                <p className="reserve__error">{quoteError}</p>
              )}

              {quoteStatus === 'success' && quote && (
                <div className="reserve__quote">
                  <span className="reserve__summary-label">Quote</span>

                  <div className="reserve__quote-summary">
                    <div className="reserve__quote-summary-item reserve__quote-summary-item_total">
                      <span>Total stay</span>
                      <strong>
                        {formatCurrency(quote.total, quote.currency)}
                      </strong>
                    </div>

                    {averageNightlyRate !== null && (
                      <div className="reserve__quote-summary-item">
                        <span>Average per night</span>
                        <strong>
                          {formatCurrency(averageNightlyRate, quote.currency)}
                        </strong>
                      </div>
                    )}
                  </div>

                  <a
                    href={`mailto:info@villamandragora.me?subject=Booking request Villa Mandragora&body=Hello,%0D%0A%0D%0AI would like to request availability for Villa Mandragora.%0D%0A%0D%0ACheck-in: ${checkIn}%0D%0ACheck-out: ${checkOut}%0D%0AGuests: ${guestsCount}%0D%0AChildren under 5: ${youngChildrenCount}%0D%0ABaby cot/playpen requested: ${babyCotRequested ? 'Yes' : 'No'}%0D%0ANights: ${selectedNights}%0D%0ATotal quote: ${quote.total} ${quote.currency}%0D%0A%0D%0AThank you.`}
                    className="reserve__button"
                  >
                    Send booking request
                  </a>
                </div>
              )}
            </>
          )}

          <Link to="/contact" className="reserve__contact-link">
            Contact us
          </Link>
        </aside>
      </div>
    </section>
  );
}

function CalendarMonth({
  monthDate,
  calendarMap,
  checkIn,
  checkOut,
  onDayClick,
}) {
  const days = getMonthCalendarDays(monthDate);

  return (
    <div className="reserve-month">
      <h2 className="reserve-month__title">
        {monthDate.toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric',
        })}
      </h2>

      <div className="reserve-month__weekdays">
        <span>Mon</span>
        <span>Tue</span>
        <span>Wed</span>
        <span>Thu</span>
        <span>Fri</span>
        <span>Sat</span>
        <span>Sun</span>
      </div>

      <div className="reserve-month__days">
        {days.map((day, index) => {
          if (!day) {
            return (
              <span
                className="reserve-day reserve-day_empty"
                key={`empty-${index}`}
              />
            );
          }

          const dateString = formatDate(day);
          const today = startOfDay(new Date());

          const dayData = calendarMap.get(dateString);
          const isPast = day < today;
          const isNotLoaded = !isPast && !dayData;
          const isUnavailable = Boolean(dayData && !dayData.isAvailable);
          const isCheckIn = checkIn === dateString;
          const isCheckOut = checkOut === dateString;
          const isInRange = isDateInRange(dateString, checkIn, checkOut);

          const className = [
            'reserve-day',
            isPast || isNotLoaded ? 'reserve-day_disabled' : '',
            isUnavailable ? 'reserve-day_unavailable' : '',
            isCheckIn || isCheckOut ? 'reserve-day_selected' : '',
            isInRange ? 'reserve-day_range' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              type="button"
              key={dateString}
              className={className}
              disabled={isPast || isNotLoaded || isUnavailable}
              onClick={() => onDayClick(dateString)}
              title={
                dayData
                  ? `${formatDisplayDate(dateString)} — ${
                      dayData.isAvailable ? 'Available' : 'Unavailable'
                    }`
                  : formatDisplayDate(dateString)
              }
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function normalizeGuestyCalendar(data) {
  const rawDays =
    data?.days ||
    data?.calendar ||
    data?.data?.days ||
    data?.data ||
    data ||
    [];

  if (!Array.isArray(rawDays)) {
    return [];
  }

  return rawDays
    .map((day) => {
      const date =
        day.date ||
        day.dateLocalized ||
        day.startDate ||
        day.start ||
        day.day;

      const status =
        day.status ||
        day.availability ||
        day.availabilityStatus ||
        day.bookingStatus ||
        '';

      const normalizedStatus = String(status).toLowerCase();

      const allotment =
        typeof day.allotment === 'number'
          ? day.allotment
          : typeof day.availableUnits === 'number'
            ? day.availableUnits
            : null;

      const isBlocked =
        day.isAvailable === false ||
        day.available === false ||
        normalizedStatus === 'unavailable' ||
        normalizedStatus === 'booked' ||
        normalizedStatus === 'blocked' ||
        normalizedStatus === 'reserved' ||
        normalizedStatus === 'not_available';

      const isAvailable =
        day.isAvailable === true ||
        day.available === true ||
        normalizedStatus === 'available' ||
        allotment > 0 ||
        !isBlocked;

      return {
        date,
        isAvailable,
        price:
          day.price ||
          day.basePrice ||
          day.nightlyPrice ||
          day.money?.fareAccommodation,
        currency: day.currency || day.money?.currency || 'EUR',
        raw: day,
      };
    })
    .filter((day) => day.date);
}

function mergeCalendarDays(currentDays, incomingDays) {
  const daysByDate = new Map(
    currentDays.map((day) => [day.date, day])
  );

  incomingDays.forEach((day) => {
    daysByDate.set(day.date, day);
  });

  return Array.from(daysByDate.values()).sort((firstDay, secondDay) =>
    firstDay.date.localeCompare(secondDay.date)
  );
}

function getMonthCalendarDays(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const firstWeekday = firstDay.getDay() === 0 ? 7 : firstDay.getDay();

  const days = [];

  for (let i = 1; i < firstWeekday; i += 1) {
    days.push(null);
  }

  for (let day = 1; day <= lastDay.getDate(); day += 1) {
    days.push(new Date(year, month, day));
  }

  return days;
}

function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function parseDate(dateString) {
  const [year, month, day] = dateString.split('-').map(Number);

  return new Date(year, month - 1, day);
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function getDatesBetween(startDateString, endDateString) {
  const dates = [];
  const current = parseDate(startDateString);
  const end = parseDate(endDateString);

  while (current < end) {
    dates.push(formatDate(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
}

function getNightsCount(startDateString, endDateString) {
  const start = parseDate(startDateString);
  const end = parseDate(endDateString);

  return Math.max(0, Math.round((end - start) / 86400000));
}

function isDateInRange(dateString, checkIn, checkOut) {
  if (!checkIn || !checkOut) return false;

  const date = parseDate(dateString);
  const start = parseDate(checkIn);
  const end = parseDate(checkOut);

  return date > start && date < end;
}

function formatCurrency(value, currency = 'EUR') {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return '—';
  }

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  }).format(amount);
}

function formatDisplayDate(dateString) {
  return parseDate(dateString).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default Reserve;
